import {
  DASHBOARD_URL_SEGMENTS,
  DASHBOARDS_API_PATH,
  dashboardSubUrl,
  dashboardUrl,
} from "../constants/dashboards";
import type {
  IAddDashboardVersionProps,
  ICreateDashboardRequest,
  ICreateDashboardShareProps,
  IDashboardContent,
  IDashboardDeleteResponse,
  IDashboardDetails,
  IDashboardListItem,
  IDashboardRefreshSummary,
  IDashboardShareCandidate,
  IDashboardSharesResponse,
  IDashboardUuidProps,
  IDashboardVersion,
  IDashboardVersionContent,
  IDashboardVersionProps,
  IDuplicateDashboardProps,
  IGetDashboardContentProps,
  IListDashboardShareCandidatesProps,
  IListDashboardsProps,
  IRefreshDashboardProps,
  IRevokeDashboardShareProps,
  ISetDashboardPinProps,
  ISetDashboardTeamAccessProps,
  ISetDashboardTeamPinProps,
  IUpdateDashboardProps,
  IUpdateDashboardScheduleProps,
  IUpdateDashboardShareProps,
} from "../types/dashboards";
import type { ApiClient } from "./client";

// NOTE: NSAI-5657 / NSAI-5658 — the saved dashboards client shared by web and
// mobile. It makes no authorization decision: the server answers every call
// with the caller's own role, and a dashboard the caller may not see is a 404
// exactly like one that does not exist.
export class DashboardsApi {
  constructor(private readonly apiClient: ApiClient) {}

  async list({ isArchived = false }: IListDashboardsProps = {}): Promise<
    IDashboardListItem[]
  > {
    const response = await this.apiClient.get<IDashboardListItem[]>(
      DASHBOARDS_API_PATH,
      { params: { archived: isArchived } },
    );
    return response.data;
  }

  async create(payload: ICreateDashboardRequest): Promise<IDashboardDetails> {
    const response = await this.apiClient.post<IDashboardDetails>(
      DASHBOARDS_API_PATH,
      payload,
    );
    return response.data;
  }

  async get({
    dashboardUuid,
  }: IDashboardUuidProps): Promise<IDashboardDetails> {
    const response = await this.apiClient.get<IDashboardDetails>(
      dashboardUrl(dashboardUuid),
    );
    return response.data;
  }

  async update({
    dashboardUuid,
    payload,
  }: IUpdateDashboardProps): Promise<IDashboardDetails> {
    const response = await this.apiClient.patch<IDashboardDetails>(
      dashboardUrl(dashboardUuid),
      payload,
    );
    return response.data;
  }

  async delete({
    dashboardUuid,
  }: IDashboardUuidProps): Promise<IDashboardDeleteResponse> {
    const response = await this.apiClient.delete<IDashboardDeleteResponse>(
      dashboardUrl(dashboardUuid),
    );
    return response.data;
  }

  async archive({
    dashboardUuid,
  }: IDashboardUuidProps): Promise<IDashboardDetails> {
    const response = await this.apiClient.post<IDashboardDetails>(
      dashboardSubUrl({
        dashboardUuid,
        segment: DASHBOARD_URL_SEGMENTS.ARCHIVE,
      }),
    );
    return response.data;
  }

  async unarchive({
    dashboardUuid,
  }: IDashboardUuidProps): Promise<IDashboardDetails> {
    const response = await this.apiClient.post<IDashboardDetails>(
      dashboardSubUrl({
        dashboardUuid,
        segment: DASHBOARD_URL_SEGMENTS.UNARCHIVE,
      }),
    );
    return response.data;
  }

  async duplicate({
    dashboardUuid,
    payload,
  }: IDuplicateDashboardProps): Promise<IDashboardDetails> {
    const response = await this.apiClient.post<IDashboardDetails>(
      dashboardSubUrl({
        dashboardUuid,
        segment: DASHBOARD_URL_SEGMENTS.DUPLICATE,
      }),
      payload,
    );
    return response.data;
  }

  async setPin({
    dashboardUuid,
    payload,
  }: ISetDashboardPinProps): Promise<IDashboardDetails> {
    const response = await this.apiClient.put<IDashboardDetails>(
      dashboardSubUrl({ dashboardUuid, segment: DASHBOARD_URL_SEGMENTS.PIN }),
      payload,
    );
    return response.data;
  }

  async setTeamPin({
    dashboardUuid,
    payload,
  }: ISetDashboardTeamPinProps): Promise<IDashboardDetails> {
    const response = await this.apiClient.put<IDashboardDetails>(
      dashboardSubUrl({
        dashboardUuid,
        segment: DASHBOARD_URL_SEGMENTS.TEAM_PIN,
      }),
      payload,
    );
    return response.data;
  }

  async updateSchedule({
    dashboardUuid,
    payload,
  }: IUpdateDashboardScheduleProps): Promise<IDashboardDetails> {
    const response = await this.apiClient.put<IDashboardDetails>(
      dashboardSubUrl({
        dashboardUuid,
        segment: DASHBOARD_URL_SEGMENTS.SCHEDULE,
      }),
      payload,
    );
    return response.data;
  }

  // NOTE: `isPreview` is for a list card's thumbnail: the server then does
  // not count the fetch as the caller opening the dashboard, so scrolling
  // the list never reorders "recently opened".
  async getContent({
    dashboardUuid,
    isPreview = false,
  }: IGetDashboardContentProps): Promise<IDashboardContent> {
    const response = await this.apiClient.get<IDashboardContent>(
      dashboardSubUrl({
        dashboardUuid,
        segment: DASHBOARD_URL_SEGMENTS.CONTENT,
      }),
      isPreview ? { params: { preview: true } } : undefined,
    );
    return response.data;
  }

  async refresh({
    dashboardUuid,
    payload,
  }: IRefreshDashboardProps): Promise<IDashboardRefreshSummary> {
    const response = await this.apiClient.post<IDashboardRefreshSummary>(
      dashboardSubUrl({
        dashboardUuid,
        segment: DASHBOARD_URL_SEGMENTS.REFRESH,
      }),
      payload,
    );
    return response.data;
  }

  async getLatestRefresh({
    dashboardUuid,
  }: IDashboardUuidProps): Promise<IDashboardRefreshSummary | null> {
    const response = await this.apiClient.get<IDashboardRefreshSummary | null>(
      dashboardSubUrl({
        dashboardUuid,
        segment: DASHBOARD_URL_SEGMENTS.REFRESH,
      }),
    );
    // NOTE: A JSON `null` body can arrive as an empty string through some
    // proxies; both mean "never refreshed".
    return response.data || null;
  }

  async listVersions({
    dashboardUuid,
  }: IDashboardUuidProps): Promise<IDashboardVersion[]> {
    const response = await this.apiClient.get<IDashboardVersion[]>(
      dashboardSubUrl({
        dashboardUuid,
        segment: DASHBOARD_URL_SEGMENTS.VERSIONS,
      }),
    );
    return response.data;
  }

  async addVersion({
    dashboardUuid,
    payload,
  }: IAddDashboardVersionProps): Promise<IDashboardDetails> {
    const response = await this.apiClient.post<IDashboardDetails>(
      dashboardSubUrl({
        dashboardUuid,
        segment: DASHBOARD_URL_SEGMENTS.VERSIONS,
      }),
      payload,
    );
    return response.data;
  }

  async getVersionContent({
    dashboardUuid,
    versionUuid,
  }: IDashboardVersionProps): Promise<IDashboardVersionContent> {
    const response = await this.apiClient.get<IDashboardVersionContent>(
      `${dashboardSubUrl({
        dashboardUuid,
        segment: DASHBOARD_URL_SEGMENTS.VERSIONS,
      })}/${encodeURIComponent(versionUuid)}/${DASHBOARD_URL_SEGMENTS.CONTENT}`,
    );
    return response.data;
  }

  async restoreVersion({
    dashboardUuid,
    versionUuid,
  }: IDashboardVersionProps): Promise<IDashboardDetails> {
    const response = await this.apiClient.post<IDashboardDetails>(
      `${dashboardSubUrl({
        dashboardUuid,
        segment: DASHBOARD_URL_SEGMENTS.VERSIONS,
      })}/${encodeURIComponent(versionUuid)}/restore`,
    );
    return response.data;
  }

  async listShares({
    dashboardUuid,
  }: IDashboardUuidProps): Promise<IDashboardSharesResponse> {
    const response = await this.apiClient.get<IDashboardSharesResponse>(
      dashboardSubUrl({
        dashboardUuid,
        segment: DASHBOARD_URL_SEGMENTS.SHARES,
      }),
    );
    return response.data;
  }

  async createShare({
    dashboardUuid,
    payload,
  }: ICreateDashboardShareProps): Promise<IDashboardSharesResponse> {
    const response = await this.apiClient.post<IDashboardSharesResponse>(
      dashboardSubUrl({
        dashboardUuid,
        segment: DASHBOARD_URL_SEGMENTS.SHARES,
      }),
      payload,
    );
    return response.data;
  }

  async updateShare({
    dashboardUuid,
    shareUuid,
    payload,
  }: IUpdateDashboardShareProps): Promise<IDashboardSharesResponse> {
    const response = await this.apiClient.patch<IDashboardSharesResponse>(
      `${dashboardSubUrl({
        dashboardUuid,
        segment: DASHBOARD_URL_SEGMENTS.SHARES,
      })}/${encodeURIComponent(shareUuid)}`,
      payload,
    );
    return response.data;
  }

  async revokeShare({
    dashboardUuid,
    shareUuid,
  }: IRevokeDashboardShareProps): Promise<IDashboardSharesResponse> {
    const response = await this.apiClient.delete<IDashboardSharesResponse>(
      `${dashboardSubUrl({
        dashboardUuid,
        segment: DASHBOARD_URL_SEGMENTS.SHARES,
      })}/${encodeURIComponent(shareUuid)}`,
    );
    return response.data;
  }

  async setTeamAccess({
    dashboardUuid,
    payload,
  }: ISetDashboardTeamAccessProps): Promise<IDashboardSharesResponse> {
    const response = await this.apiClient.put<IDashboardSharesResponse>(
      dashboardSubUrl({
        dashboardUuid,
        segment: DASHBOARD_URL_SEGMENTS.TEAM_ACCESS,
      }),
      payload,
    );
    return response.data;
  }

  async listShareCandidates({
    dashboardUuid,
    search,
  }: IListDashboardShareCandidatesProps): Promise<IDashboardShareCandidate[]> {
    const trimmedSearch = search?.trim() ?? "";
    const response = await this.apiClient.get<IDashboardShareCandidate[]>(
      dashboardSubUrl({
        dashboardUuid,
        segment: DASHBOARD_URL_SEGMENTS.SHARE_CANDIDATES,
      }),
      trimmedSearch ? { params: { search: trimmedSearch } } : undefined,
    );
    return response.data;
  }
}
